(() => {
  'use strict';
  if (customElements.get('customer-address-book')) return;
  class CustomerAddressBook extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.words = JSON.parse(this.querySelector('[data-translations]').textContent);
      this.endpoint = this.dataset.endpoint.replace(/\/$/, '');
      this.status = this.querySelector('[data-status]');
      if (!this.status) return;
      this.controller = new AbortController();
      this.addresses = [];
      this.originalButtons = new Map();
      this.ready = false;
      this.busy = false;
      this.load().catch(error => this.message(error.message, true));
    }
    disconnectedCallback() { this.controller?.abort(); this.initialized = false; }
    message(message, error = false) { this.status.textContent = message; this.status.dataset.error = String(error); }
    async request(path, payload) {
      const response = await fetch(this.endpoint + path, {
        method: payload ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: payload ? { 'Content-Type': 'application/json' } : {},
        body: payload ? JSON.stringify({ ...payload, csrfToken: this.csrfToken }) : undefined,
        signal: this.controller.signal
      });
      let data;
      try { data = await response.json(); } catch { throw new Error(this.words.unavailable); }
      if (!response.ok) { const error = new Error(data.error || this.words.unavailable); error.status = response.status; throw error; }
      return data;
    }
    storage(key, value) {
      try { const name = 'address-book:' + this.accountKey + ':' + key; if (value === undefined) return JSON.parse(sessionStorage.getItem(name) || 'null'); if (value === null) sessionStorage.removeItem(name); else sessionStorage.setItem(name, JSON.stringify(value)); } catch { return null; }
    }
    async load() {
      const state = await this.request('/state');
      this.addresses = state.addresses;
      this.csrfToken = state.csrfToken;
      this.accountKey = state.accountKey;
      this.ready = true;
      if (this.dataset.mode === 'manage') this.setupManager();
      else this.setupCart();
      this.message(this.addresses.length ? '' : this.words.empty);
    }
    display(address) {
      return [address.company, [address.firstName,address.lastName].filter(Boolean).join(' '), address.address1, address.address2, [address.zip,address.city].join(' '), address.countryCode].filter(Boolean).join('\n');
    }
    options(select, entries, selected, placeholder = this.words.choose) {
      select.replaceChildren(new Option(placeholder, ''));
      entries.forEach(address => select.add(new Option(address.label, address.id)));
      select.value = entries.some(address => address.id === selected) ? selected : '';
    }
    setupManager() {
      this.editor = this.querySelector('[data-editor]');
      this.dialog = this.querySelector('dialog:not([data-delete-dialog])');
      this.deleteDialog = this.querySelector('[data-delete-dialog]');
      const options = { signal: this.controller.signal };
      this.querySelector('[data-add]').addEventListener('click', () => this.edit(), options);
      this.querySelector('[data-cancel]').addEventListener('click', () => this.dialog.close(), options);
      this.querySelector('[data-close-delete]').addEventListener('click', () => this.deleteDialog.close(), options);
      this.editor.elements.kind.addEventListener('change', () => this.assignments(), options);
      this.editor.addEventListener('submit', event => { event.preventDefault(); this.save(); }, options);
      this.querySelector('[data-confirm-delete]').addEventListener('click', () => this.remove(), options);
      this.querySelector('[data-address-list]').addEventListener('click', event => {
        const button = event.target.closest('[data-action]');
        if (!button) return;
        const address = this.addresses.find(entry => entry.id === button.dataset.id);
        if (!address) return;
        if (button.dataset.action === 'edit') this.edit(address);
        else { this.deleting = address; this.querySelector('[data-delete-label]').textContent = address.label; this.querySelector('[data-delete-error]').hidden = true; this.deleteDialog.showModal(); }
      }, options);
      this.renderList();
    }
    renderList() {
      const list = this.querySelector('[data-address-list]');
      list.replaceChildren();
      for (const address of this.addresses) {
        const card = document.createElement('article'); card.className = 'address-book__card';
        const heading = document.createElement('h2'); heading.textContent = address.label;
        const badge = document.createElement('span'); badge.className = 'address-book__badge'; badge.textContent = this.words[address.kind];
        const details = document.createElement('address'); details.textContent = this.display(address);
        card.append(heading,badge,details);
        const billing = this.addresses.find(entry => entry.id === address.billingAddressId);
        if (billing) { const assignment = document.createElement('p'); assignment.textContent = this.words.assignment + ': ' + billing.label; card.append(assignment); }
        const actions = document.createElement('div'); actions.className = 'address-book__actions';
        for (const action of ['edit','delete']) { const button = document.createElement('button'); button.type = 'button'; button.dataset.action = action; button.dataset.id = address.id; button.textContent = this.words[action]; actions.append(button); }
        card.append(actions); list.append(card);
      }
    }
    assignments(selected) {
      const label = this.querySelector('[data-assignment-label]');
      const select = this.editor.elements.billingAddressId;
      label.hidden = this.editor.elements.kind.value === 'billing';
      this.options(select, this.addresses.filter(entry => entry.kind !== 'shipping' && entry.id !== this.editing?.id), selected === undefined ? select.value : selected, this.words.none);
    }
    edit(address) {
      this.editing = address || null;
      this.editor.reset();
      if (address) for (const field of this.editor.elements) if (field.name && field.name !== 'billingAddressId') field.value = address[field.name] || '';
      this.assignments(address?.billingAddressId || '');
      this.querySelector('[data-editor-title]').textContent = address ? this.words.edit : this.words.add;
      this.querySelector('[data-form-error]').hidden = true;
      this.dialog.showModal();
      this.editor.elements.label.focus();
    }
    async save() {
      if (this.busy || !this.editor.reportValidity()) return;
      this.busy = true;
      const button = this.editor.querySelector('[type="submit"]'); button.disabled = true;
      try {
        const address = Object.fromEntries(new FormData(this.editor));
        const result = await this.request('/addresses', { address, id: this.editing?.id, version: this.editing?.version });
        this.addresses = result.addresses;
        this.renderList(); this.dialog.close(); this.message(this.words.saved);
      } catch (error) { const el = this.querySelector('[data-form-error]'); el.textContent = error.message; el.hidden = false; }
      finally { this.busy = false; button.disabled = false; }
    }
    async remove() {
      if (this.busy || !this.deleting) return;
      this.busy = true;
      const button = this.querySelector('[data-confirm-delete]'); button.disabled = true;
      try {
        const result = await this.request('/addresses/delete', { id: this.deleting.id, version: this.deleting.version });
        this.addresses = result.addresses; this.renderList(); this.deleteDialog.close(); this.message(this.words.deleted);
      } catch (error) { const el = this.querySelector('[data-delete-error]'); el.textContent = error.message; el.hidden = false; }
      finally { this.busy = false; button.disabled = false; }
    }
    setupCart() {
      const options = { signal: this.controller.signal };
      this.form = document.getElementById(this.dataset.formId);
      this.toggle = this.querySelector('[data-use-addresses]');
      this.shipping = this.querySelector('[data-shipping]');
      this.billing = this.querySelector('[data-billing]');
      const selection = this.storage('selection');
      this.options(this.shipping, this.addresses.filter(a => a.kind !== 'billing'), selection?.shippingId);
      this.options(this.billing, this.addresses.filter(a => a.kind !== 'shipping'), selection?.billingId);
      this.toggle.disabled = false;
      this.toggle.checked = Boolean(selection?.enabled);
      const refresh = () => {
        this.querySelector('[data-selection]').hidden = !this.toggle.checked;
        for (const type of ['shipping','billing']) {
          const address = this.addresses.find(entry => entry.id === this[type].value);
          this.querySelector('[data-' + type + '-summary]').textContent = address ? this.display(address) : '';
        }
        this.storage('selection', { enabled: this.toggle.checked, shippingId: this.shipping.value, billingId: this.billing.value });
        document.querySelectorAll('button[name="checkout"],button[data-address-book-checkout]').forEach(button => {
          if (button.form === this.form) {
            if (!this.originalButtons.has(button)) this.originalButtons.set(button, { nodes: [...button.childNodes].map(node => node.cloneNode(true)), type: button.getAttribute('type'), name: button.name });
            const original = this.originalButtons.get(button);
            button.dataset.addressBookCheckout = '';
            if (this.toggle.checked) {
              button.name = 'address_book_checkout';
              button.type = 'button';
              button.textContent = this.words.next;
            } else {
              button.name = original.name;
              if (original.type === null) button.removeAttribute('type');
              else button.setAttribute('type', original.type);
              button.replaceChildren(...original.nodes.map(node => node.cloneNode(true)));
            }
          }
        });
      };
      this.toggle.addEventListener('change', refresh, options);
      this.billing.addEventListener('change', refresh, options);
      this.shipping.addEventListener('change', () => {
        const address = this.addresses.find(entry => entry.id === this.shipping.value);
        if (address?.billingAddressId) this.billing.value = address.billingAddressId;
        refresh();
      }, options);
      document.addEventListener('click', event => {
        const button = event.target.closest('button[name="checkout"],button[data-address-book-checkout]');
        if (!button || button.form !== this.form || !this.toggle.checked) return;
        event.preventDefault(); event.stopImmediatePropagation(); this.checkout(button);
      }, { ...options, capture: true });
      this.form?.addEventListener('submit', event => {
        if (!this.toggle.checked || !event.submitter?.matches('button[name="checkout"],button[data-address-book-checkout]')) return;
        event.preventDefault(); event.stopImmediatePropagation(); this.checkout(event.submitter);
      }, { ...options, capture: true });
      refresh();
      this.reconciling = this.reconcile().catch(() => {}).finally(() => { this.reconciling = null; });
    }
    async cart() {
      const response = await fetch(this.dataset.rootUrl + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error(this.words.unavailable);
      return response.json();
    }
    cartPayload(cart) {
      return {
        currency: cart.currency,
        items: cart.items.map(item => ({ variant_id: String(item.variant_id), quantity: item.quantity, properties: item.properties, selling_plan_allocation: item.selling_plan_allocation, item_components: item.item_components, parent_relationship: item.parent_relationship })),
        attributes: cart.attributes || {}, note: cart.note || '',
        discountCodes: [...new Set([...(cart.discount_codes || []).map(code => code.code), ...(cart.items || []).flatMap(item => item.line_level_discount_allocations || []).map(allocation => allocation.discount_application).filter(discount => discount?.type === 'discount_code').map(discount => ({ code: discount.title } )).map(code => code.code), ...(cart.cart_level_discount_applications || []).filter(discount => discount.type === 'discount_code').map(discount => discount.title)])].filter(Boolean)
      };
    }
    async hash(data) {
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(data)));
      return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2,'0')).join('');
    }
    async reconcile() {
      const pending = this.storage('checkout');
      if (!pending) return;
      const result = await this.request('/checkout-status', { requestId: pending.requestId });
      if (result.status === 'COMPLETED') {
        this.storage('checkout', null);
        const cart = await this.cart();
        if (pending.cartHash === await this.hash(this.cartPayload(cart))) {
          const response = await fetch(this.dataset.rootUrl + 'cart/clear.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: '{}' });
          if (response.ok) location.reload();
        }
      }
    }
    async checkout(button) {
      if (this.busy) return;
      if (this.reconciling) await this.reconciling;
      if (this.busy) return;
      if (!this.ready || !this.shipping.value || !this.billing.value) return this.message(this.words.needAddresses, true);
      if (!this.form?.reportValidity()) return;
      const terms = this.form.querySelector('.js-cart-terms-checkbox') || document.querySelector('#cart-terms');
      if (terms && !terms.checked) return this.message(this.words.terms, true);
      this.busy = true; button.disabled = true; this.message(this.words.redirecting);
      try {
        const attributes = {};
        document.querySelectorAll('input[name^="attributes["],textarea[name^="attributes["]').forEach(input => {
          if (input.form === this.form) { const match = input.name.match(/^attributes\[(.+)\]$/); if (match) attributes[match[1]] = input.value; }
        });
        const note = document.querySelector('[name="note"][form="' + this.dataset.formId + '"]') || this.form.querySelector('[name="note"]');
        if (Object.keys(attributes).length || note) {
          const result = await fetch(this.dataset.rootUrl + 'cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes, ...(note ? { note: note.value } : {}) }) });
          if (!result.ok) throw new Error(this.words.unavailable);
        }
        const snapshot = await this.cart();
        const quantities = [...document.querySelectorAll('input[name="updates[]"]')].filter(input => input.form === this.form);
        if (quantities.length && quantities.some((input,index) => snapshot.items[index] && Number(input.value) !== snapshot.items[index].quantity)) throw new Error(this.words.cartChanged);
        const cart = this.cartPayload(snapshot);
        const selected = { shippingId: this.shipping.value, billingId: this.billing.value, cart };
        const fingerprint = await this.hash(selected);
        const previous = this.storage('checkout');
        const requestId = previous?.fingerprint === fingerprint ? previous.requestId : crypto.randomUUID();
        const pending = { requestId, fingerprint, cartHash: await this.hash(cart) };
        this.storage('checkout', pending);
        const result = await this.request('/checkout', { ...selected, requestId });
        const url = new URL(result.checkoutUrl);
        if (url.protocol !== 'https:' || !(url.hostname === location.hostname || /^[a-z0-9-]+\.myshopify\.com$/.test(url.hostname))) throw new Error(this.words.unavailable);
        location.assign(url.href);
      } catch (error) { if (error.status === 422) this.storage('checkout', null); this.message(error.message || this.words.unavailable, true); }
      finally { this.busy = false; button.disabled = false; }
    }
  }
  customElements.define('customer-address-book', CustomerAddressBook);
})();
